import React, { useRef, useEffect } from 'react';
import { Delaunay } from 'd3-delaunay';

const DynamicVoronoi = (props) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    let centerX = width / 2;
    let centerY = height / 2;
    let maxRadius = Math.max(width, height) * 0.9;

    // Global rotation angle (all points will rotate around the center)
    let rotation = 0;
    const rotationSpeed = 0.00022;

    // Number of points
    const numPoints = 58;
    // Each point is defined by a random angle and radial distance from the center.
    const points = [];
    for (let i = 0; i < numPoints; i++) {
      points.push({
        angle: Math.random() * 2 * Math.PI,
        r: Math.random() * maxRadius,
      });
    }

    function draw() {
      // Update the global rotation
      rotation += rotationSpeed;

      // Clear the canvas with the dark gray background
      ctx.fillStyle = 'rgb(14, 14, 14)';
      ctx.fillRect(0, 0, width, height);

      // Calculate current x,y for each point after applying the rotation
      const coords = points.map((p) => {
        return [
          centerX + p.r * Math.cos(p.angle + rotation),
          centerY + p.r * Math.sin(p.angle + rotation),
        ];
      });

      // Compute the Voronoi diagram using the rotated points
      const delaunay = Delaunay.from(coords);
      const voronoi = delaunay.voronoi([0, 0, width, height]);

      // Set the style for cell boundaries and vertices (light gray)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.13)';
      ctx.lineWidth = 0.8;

      // Draw each Voronoi cell
      for (let i = 0; i < points.length; i++) {
        const cell = voronoi.cellPolygon(i);
        if (cell) {
          ctx.beginPath();
          ctx.moveTo(cell[0][0], cell[0][1]);
          for (let j = 1; j < cell.length; j++) {
            ctx.lineTo(cell[j][0], cell[j][1]);
          }
          ctx.closePath();
          // Fill the cell with dark gray to match the background
          const distance = Math.hypot(coords[i][0] - centerX, coords[i][1] - centerY);
          const warmth = Math.max(0, 1 - distance / Math.max(width, height));
          ctx.fillStyle = `rgba(${18 + Math.round(warmth * 12)}, ${17 + Math.round(warmth * 3)}, 16, 1)`;
          ctx.fill();
          // Draw the cell border in light gray
          ctx.stroke();

          // Draw the vertices as small light gray circles
          for (let j = 0; j < cell.length; j++) {
            const vx = cell[j][0];
            const vy = cell[j][1];
            ctx.beginPath();
            ctx.arc(vx, vy, 1.1, 0, 2 * Math.PI);
            ctx.fillStyle = 'rgba(200, 255, 77, 0.28)';
            ctx.fill();
          }
        }
      }

      requestAnimationFrame(draw);
    }

    draw();

    // Handle canvas resize
    function handleResize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      centerX = width / 2;
      centerY = height / 2;
      maxRadius = Math.max(width, height) * 0.9;
    }
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Apply the passed className prop to the canvas
  return <canvas ref={canvasRef} className={props.className} style={{ display: 'block' }} />;
};

export default DynamicVoronoi;
